import React, { useCallback, useEffect, useRef, useState } from 'react';
import './../../App.css';
import './SearchPage.css';
import PageShell from '../../components/layout/PageShell';
import PageHeader from '../../components/layout/PageHeader';
import ApiService from '../../services/ApiService';
import AnimeCardComponent from '../../components/animecard/AnimeCardComponent';
import RatingRangeSlider from './RatingRageSlider';
import searchIcon from '../../assets/ic_search.svg';

const GENRES = [
    "Авангард", "Гурман", "Дзёсей", "Драма", "Комедия", "Музыка", "Повседневность",
    "Приключения", "Романтика", "Сверхъестественное", "Сёдзё", "Сёнен",
    "Спорт", "Сэйнэн", "Тайна", "Триллер", "Ужасы", "Фантастика",
    "Фэнтези", "Экшен", "Эротика", "Этти"
];

const FORMAT_OPTIONS = [
    { key: 'movie', label: 'Полнометражные' },
    { key: 'hq', label: 'QHD и 4K' },
    { key: 'viral', label: 'Вирусное' },
];

const DEFAULT_FILTERS = {
    query: '',
    genres: [],
    movie: false,
    hq: false,
    viral: false,
    minRating: 0,
    maxRating: 10,
};

const buildQueryParams = (filters, pageNum) => {
    const params = new URLSearchParams();
    params.append('page', pageNum);
    if (filters.query) params.append('query', filters.query);
    if (filters.genres.length > 0) params.append('genres', filters.genres.join(','));
    if (filters.movie) params.append('movie', true);
    if (filters.hq) params.append('hq', true);
    if (filters.minRating > 0) params.append('minRating', filters.minRating);
    if (filters.maxRating < 10) params.append('maxRating', filters.maxRating);
    if (filters.viral) params.append('viral', true);
    return params.toString();
};

const countActiveFilters = (filters) => (
    filters.genres.length
    + FORMAT_OPTIONS.filter((option) => filters[option.key]).length
    + (filters.minRating > 0 || filters.maxRating < 10 ? 1 : 0)
);

const SearchPage = () => {
    // `filters` drives requests; `queryInput` and `ratingInput` are the live controls, applied with a debounce.
    const [filters, setFilters] = useState(DEFAULT_FILTERS);
    const [queryInput, setQueryInput] = useState('');
    const [ratingInput, setRatingInput] = useState({ min: 0, max: 10 });
    const [sliderKey, setSliderKey] = useState(0);
    const [filtersOpen, setFiltersOpen] = useState(false);

    const [animeList, setAnimeList] = useState([]);
    const [page, setPage] = useState(0);
    const [loading, setLoading] = useState(false);
    const [thatsAll, setThatsAll] = useState(false);

    const requestIdRef = useRef(0);
    const sentinelRef = useRef(null);
    const queryTimerRef = useRef(null);
    const ratingTimerRef = useRef(null);

    useEffect(() => {
        document.title = 'animix - Поиск';
        return () => {
            clearTimeout(queryTimerRef.current);
            clearTimeout(ratingTimerRef.current);
        };
    }, []);

    // New filters start a fresh result list.
    useEffect(() => {
        setAnimeList([]);
        setThatsAll(false);
        setPage(0);
    }, [filters]);

    // Load the current page; responses for outdated filters are dropped.
    useEffect(() => {
        const requestId = ++requestIdRef.current;
        setLoading(true);

        ApiService.searchFilteredAnime(buildQueryParams(filters, page))
            .then((data) => {
                if (requestId !== requestIdRef.current) return;
                const items = data?.type === 'success' ? (data.data.anime || []) : [];
                if (items.length === 0) {
                    setThatsAll(true);
                } else {
                    setAnimeList((prev) => (page === 0 ? items : [...prev, ...items]));
                }
            })
            .catch((error) => {
                if (requestId === requestIdRef.current) {
                    console.error('Search error', error);
                    setThatsAll(true);
                }
            })
            .finally(() => {
                if (requestId === requestIdRef.current) setLoading(false);
            });
    }, [filters, page]);

    // Next page whenever the end of the grid is near — also fills a screen that is taller than the results.
    useEffect(() => {
        const sentinel = sentinelRef.current;
        if (!sentinel || loading || thatsAll) return undefined;
        const observer = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting) setPage((prev) => prev + 1);
        }, { rootMargin: '600px 0px' });
        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [loading, thatsAll, animeList.length]);

    const updateFilters = useCallback((patch) => {
        setFilters((prev) => ({ ...prev, ...patch }));
    }, []);

    const handleQueryChange = (value) => {
        setQueryInput(value);
        clearTimeout(queryTimerRef.current);
        queryTimerRef.current = setTimeout(() => updateFilters({ query: value.trim() }), 450);
    };

    const clearQuery = () => {
        setQueryInput('');
        clearTimeout(queryTimerRef.current);
        updateFilters({ query: '' });
    };

    const toggleGenre = (genre) => {
        setFilters((prev) => ({
            ...prev,
            genres: prev.genres.includes(genre)
                ? prev.genres.filter((item) => item !== genre)
                : [...prev.genres, genre],
        }));
    };

    const handleRatingChange = (range) => {
        setRatingInput(range);
        clearTimeout(ratingTimerRef.current);
        ratingTimerRef.current = setTimeout(() => updateFilters({ minRating: range.min, maxRating: range.max }), 400);
    };

    const resetFilters = () => {
        clearTimeout(ratingTimerRef.current);
        setRatingInput({ min: 0, max: 10 });
        setSliderKey((key) => key + 1);
        setFilters((prev) => ({ ...DEFAULT_FILTERS, query: prev.query }));
    };

    const activeCount = countActiveFilters(filters);
    const ratingLabel = ratingInput.min === 0 && ratingInput.max === 10
        ? 'Любой'
        : `${ratingInput.min} – ${ratingInput.max}`;

    return (
        <PageShell className="search-page">
            <PageHeader title="Поиск" />

            <div className="search-layout">
                <aside className={`search-filters ${filtersOpen ? 'is-open' : ''}`} aria-label="Фильтры">
                    <div className="search-filters__head">
                        <h2>Фильтры</h2>
                        {activeCount > 0 ? (
                            <button type="button" className="search-filters__reset" onClick={resetFilters}>
                                Сбросить
                            </button>
                        ) : null}
                    </div>

                    <section className="search-filters__section">
                        <h3>Жанры</h3>
                        <div className="filter-chips">
                            {GENRES.map((genre) => (
                                <button
                                    key={genre}
                                    type="button"
                                    className="ui-chip"
                                    aria-pressed={filters.genres.includes(genre)}
                                    onClick={() => toggleGenre(genre)}
                                >
                                    {genre}
                                </button>
                            ))}
                        </div>
                    </section>

                    <section className="search-filters__section">
                        <h3>Формат</h3>
                        <div className="filter-switches">
                            {FORMAT_OPTIONS.map((option) => (
                                <button
                                    key={option.key}
                                    type="button"
                                    role="switch"
                                    aria-checked={filters[option.key]}
                                    className="filter-switch"
                                    onClick={() => updateFilters({ [option.key]: !filters[option.key] })}
                                >
                                    <span>{option.label}</span>
                                    <span className="ui-switch" aria-hidden="true" />
                                </button>
                            ))}
                        </div>
                    </section>

                    <section className="search-filters__section">
                        <h3>
                            Рейтинг <span className="search-filters__value">{ratingLabel}</span>
                        </h3>
                        <RatingRangeSlider
                            key={sliderKey}
                            minValue={ratingInput.min}
                            maxValue={ratingInput.max}
                            onChange={handleRatingChange}
                        />
                    </section>
                </aside>

                <div className={`search-main${loading ? ' is-loading' : ''}`}>
                    <div className="search-field">
                        <img src={searchIcon} alt="" aria-hidden="true" className="search-field__icon" />
                        <input
                            type="search"
                            placeholder="Название тайтла"
                            aria-label="Название тайтла"
                            value={queryInput}
                            onChange={(event) => handleQueryChange(event.target.value)}
                        />
                        {queryInput ? (
                            <button type="button" className="search-field__clear ui-close ui-close--sm ui-close--round ui-close--ghost" onClick={clearQuery} aria-label="Очистить">
                                ×
                            </button>
                        ) : null}
                    </div>

                    <div className="search-results-bar">
                        {/* Keyed by the text so a new count fades in instead of snapping. */}
                        <p key={loading && animeList.length === 0 ? 'searching' : animeList.length}>
                            {loading && animeList.length === 0
                                ? 'Ищем…'
                                : `${thatsAll ? 'Найдено' : 'Показано'}: ${animeList.length}`}
                        </p>
                        <button
                            type="button"
                            className="ui-chip search-filters-toggle"
                            aria-expanded={filtersOpen}
                            onClick={() => setFiltersOpen((open) => !open)}
                        >
                            Фильтры
                            {activeCount > 0 ? <span key={activeCount} className="search-count">{activeCount}</span> : null}
                        </button>
                    </div>

                    <div className="anime-container">
                        {animeList.map((anime, index) => (
                            <AnimeCardComponent key={`${anime.id || index}`} anime={anime} isInteractive={true} />
                        ))}
                    </div>

                    <div ref={sentinelRef} aria-hidden="true" />
                    {loading && animeList.length > 0 ? <p className="ui-status">Загружаем…</p> : null}
                    {thatsAll && animeList.length === 0 ? (
                        <div className="ui-empty ui-empty--search">
                            <h2 className="ui-empty__title">Ничего не найдено</h2>
                            <p className="ui-empty__text">Попробуйте другое название или ослабьте фильтры.</p>
                        </div>
                    ) : null}
                </div>
            </div>
        </PageShell>
    );
};

export default SearchPage;
